// Description: This component is responsible for rendering the page to add a new WhatsApp instance.
// Updated: 28/08/2025
// Author: Vitor de Jesus

// React
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Material UI Components
import {
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";

// Components and Functions for the project
import QRCODE2 from "../../assets/images/QRCODE2.png";
import { ChannelCheckQRCode } from "../../api/aws/Channels/ChannelCheckQRCode";
import { ChannelCheck } from "../../api/aws/Channels/ChannelCheck";
import { ChannelAdd } from "../../api/aws/Channels/ChannelAdd";
import StepIndicator from "../../components/DynamicForm/StepIndicator";
import StatusIcon from "../../components/StatusLabel/StatusIcon";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { notify } from "../../utils/utils";
import CampaignModal from "../../components/Modals/CampaignModal";
import themeColors from "../../components/ThemeColors/ThemeColors";
import { isAuthenticated } from "../../authMiddleware";
import use123zapStore from "../../context/123zapStore";
import ReturnToLastPage from "../../components/ChannelAdd/ReturnToLastPage.jsx";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// Render The Page
function ChannelPageAddWhatsApp() {
  const Navigate = useNavigate();

  const [firstTime, setFirstTime] = useState(30);
  const [verifyConnectionPooling, setVerifyConnectionPooling] = useState(30);
  const [poolingTimerExpired, setPoolingTimerExpired] = useState(300);

  const [instance, setInstance] = useState("");

  const [qrcode, setQrcode] = useState(QRCODE2);
  const [instanceUUID, setInstanceUUID] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const creatingCampaignWithHelpMode =
    JSON.parse(sessionStorage.getItem("creating_campaign")) || null;
  const [openModalCampaign, setOpenModalCampaign] = useState(false);
  const HandleChannels = () => {
    creatingCampaignWithHelpMode === true
      ? Navigate("/campanhas/adicionar")
      : Navigate("/canais");
  };

  const userAuthenticated = use123zapStore((state) => state.userAuthenticated);
  const setAlreadyFetchChannels = use123zapStore(
    (state) => state.setAlreadyFetchChannels
  );
  const planoAtual = use123zapStore((state) => state.planoAtual);
  const userInfo = use123zapStore((state) => state.userInfo);
  const setUserInfo = use123zapStore((state) => state.setUserInfo);

  useEffect(() => {
    if (userAuthenticated && Object.keys(userInfo).length === 0) {
      const { decryptedData } = isAuthenticated();
      setUserInfo(decryptedData);
    }
  }, [userAuthenticated]);

  useEffect(() => {
    if (
      planoAtual !== "StartZap" &&
      planoAtual !== "ProZap" &&
      planoAtual !== "UltraZap" &&
      planoAtual !== null
    ) {
      Navigate("/acesso-restrito");
    }
  }, [planoAtual]);

  const handleAdicionar = async () => {
    try {
      setFirstTime(10);
      setQrcode(QRCODE2);

      const addInstance = await ChannelAdd();
      const code = addInstance.code;

      setInstanceUUID(code);

      const newInstance = await ChannelCheckQRCode(code);

      const qrcodeUrl = newInstance.qrCode;
      setQrcode(qrcodeUrl);
    } catch (error) {
      console.error("Error connecting to instance:", error);
    } finally {
      setFirstTime(0);
    }
  };

  const HandleWhatsApp = () => {
    window.open(
      "https://wa.me/553121300297?text=Ol%C3%A1%2C%20estou%20precisando%20de%20suporte!%20",
      "_blank"
    );
  };

  const steps = [
    {
      stepNumber: 1,
      title: "Vamos adicionar seu WhatsApp?",
      description: (
        <span>
          O primeiro passo é escolher um nome para o seu WhatsApp. Este nome
          será somente para{" "}
          <strong style={{ color: "#000" }}>identificação interna</strong> na
          plataforma e não será exibido para seus clientes na conversa.
        </span>
      ),
    },
    {
      stepNumber: 2,
      title: "Realize a leitura do QR Code",
      description: (
        <span>
          Chegou a hora de conectar o seu WhatsApp. Para isso, abra o aplicativo
          WhatsApp no seu celular{" "}
          <strong style={{ color: "#000" }}> {" > "}</strong> Dispositivos
          Conectados <strong style={{ color: "#000" }}> {" > "}</strong>{" "}
          Conectar um dispositivo{" "}
          <strong style={{ color: "#000" }}> {" > "}</strong> Escanei o QR Code
          abaixo.
        </span>
      ),
    },
    {
      stepNumber: 3,
      title: "Aguarde um instante! 😉",
      description: (
        <span>
          Por favor, aguarde enquanto configuramos o seu WhatsApp. Este processo
          pode levar algum tempo.{" "}
          <strong style={{ color: "#000" }}>Não feche esta página</strong> até
          que todo o processo seja concluído.
        </span>
      ),
    },
    {
      stepNumber: 4,
      title: success
        ? "WhatsApp adicionado com sucesso!"
        : "Ops! Algo deu errado",
      description: success ? (
        <span>
          Agora você pode começar a usar o seu WhatsApp na plataforma. Clique no
          botão abaixo para acessar sua listagem de canais.
        </span>
      ) : (
        <span>
          Parece que houve um problema ao adicionar o seu WhatsApp. Por favor,
          tente novamente mais tarde ou entre em contato com o suporte para
          obter ajuda.
        </span>
      ),
    },
  ];

  const canMoveForward = () => {
    if (currentStep === 1 && instance === "") {
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (poolingTimerExpired === 0 && currentStep === 3) {
      setCurrentStep(4);
    }
  }, [poolingTimerExpired, currentStep]);

  const checkConnection = async () => {
    try {
      const code = instanceUUID;
      const name = instance;

      const resultJson = await ChannelCheck({ code, name });

      if (resultJson.status === 200 && resultJson.uuid === code) {
        setSuccess(true);
        setCurrentStep(4);
        setVerifyConnectionPooling(0);
        setAlreadyFetchChannels(false);
      } else {
        setSuccess(false);
      }
    } catch (error) {
      console.error("Error checking connection:", error);
    } finally {
      setVerifyConnectionPooling(20);
      setPoolingTimerExpired(0);
    }
  };

  useEffect(() => {
    if (currentStep === 3) {
      const timer = setInterval(() => {
        setVerifyConnectionPooling((prevTime) => prevTime - 1);
      }, 1000);

      if (verifyConnectionPooling === 0) {
        checkConnection();
      }
      return () => clearInterval(timer);
    }
  }, [verifyConnectionPooling, currentStep]);

  const FormContent = ({
    currentStep,
    steps,
    instance,
    setInstance,
    openModal,
  }) => {
    const [localInstance, setLocalInstance] = useState(instance);

    const handleInstanceChange = (e) => {
      setLocalInstance(e.target.value);
    };

    const handleBlur = () => {
      setInstance(localInstance);
    };

    if (currentStep === 1) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box sx={{ width: { xs: "100%", sm: "50%" }, margin: "0 auto" }}>
            <TextField
              value={localInstance}
              placeholder="Nome de identificação"
              onChange={handleInstanceChange}
              onBlur={handleBlur} // Atualiza o estado global quando o campo perde o foco
              sx={{
                "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                  padding: "12px",
                },
                background: error ? "#F8D6D3" : "#EBEBEB",
                borderRadius: "6px",
                width: "100%",
              }}
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 2 && qrcode !== QRCODE2 && firstTime === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Tooltip
                title={
                  <Typography variant="body2">
                    Clique no botão abaixo para tentar novamente
                  </Typography>
                }
                arrow
                placement="top"
              >
                <img
                  src={qrcode ? qrcode : QRCODE2}
                  alt="QR Code"
                  style={{ width: "200px", height: "200px" }}
                />
              </Tooltip>
              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "16px" },
                  color: "#848484",
                  textAlign: "center",
                  marginTop: "5px",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => handleAdicionar()}
              >
                Gerar novo QR Code
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    } else if (currentStep === 2 && firstTime > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            Aguarde um instante! 😉
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            Estamos gerando seu QR Code para escanear no WhatsApp. Isso pode
            levar alguns segundos. Se houver qualquer problema ou demora, entre
            em contato com o suporte.
          </Typography>

          <Box
            sx={{
              border: "2px solid #F5F3FF",
              borderRadius: "50%", // Garante que seja um círculo
              height: "200px", // Define o tamanho fixo para o círculo
              width: "200px", // Define a mesma largura para o círculo
              display: "flex",
              flexDirection: "column", // Organiza os itens em coluna
              justifyContent: "center", // Centraliza verticalmente
              alignItems: "center", // Centraliza horizontalmente
              textAlign: "center", // Alinhamento do texto centralizado
            }}
          >
            <CircularProgress
              sx={{
                position: "absolute", // Faz com que o progresso circular fique em cima do Box
                color: "#F5F3FF", // Cor do progresso circular
              }}
              size={200} // Tamanho do círculo
              thickness={1} // Espessura do progresso
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 3 && poolingTimerExpired > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              border: "2px solid #F5F3FF",
              borderRadius: "50%", // Garante que seja um círculo
              height: "200px", // Define o tamanho fixo para o círculo
              width: "200px", // Define a mesma largura para o círculo
              display: "flex",
              flexDirection: "column", // Organiza os itens em coluna
              justifyContent: "center", // Centraliza verticalmente
              alignItems: "center", // Centraliza horizontalmente
              textAlign: "center", // Alinhamento do texto centralizado
            }}
          >
            <CircularProgress
              sx={{
                position: "absolute", // Faz com que o progresso circular fique em cima do Box
                color: "#F5F3FF", // Cor do progresso circular
              }}
              size={200} // Tamanho do círculo
              thickness={1} // Espessura do progresso
            />
          </Box>
        </Box>
      );
    } else if (currentStep === 4) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: "20px", sm: "30px" },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: "30px", sm: "0px" },
            }}
          >
            <StepIndicator currentStep={currentStep} steps={steps} />
          </Box>

          <Divider
            sx={{
              width: "100%",
              borderColor: "gray",
              marginBottom: { xs: "0px", sm: "15px" },
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "1.125rem" },
              fontWeight: 700,
              color: "#000",
            }}
          >
            {steps[currentStep - 1].title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              color: "#E923B",
              marginBottom: "19px",
              textAlign: "center",
              maxWidth: { xs: "100%", sm: "80%" },
            }}
          >
            {steps[currentStep - 1].description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
            <StatusIcon
              color={"red"}
              status={success ? "emoji01" : "emoji10"}
              height={"80px"}
              width={"80px"}
            />
          </Box>

          {success ? (
            <Button
              variant="outlined"
              sx={{
                padding: "0 12px",
                minWidth: {
                  xs: "100%",
                  sm: "0px",
                },
                height: "36px",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "9999px",
                fontSize: "16px",
                fontWeight: 500,
              }}
              onClick={openModal}
              startIcon={<CheckCircleOutlineIcon sx={{ color: "#000000ff" }} />}
            >
              Verificar meus canais
            </Button>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "10px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0px",
                  },
                  height: "36px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                onClick={HandleWhatsApp}
                startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
              >
                Falar com o suporte
              </Button>

              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0px",
                  },
                  height: "36px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  Navigate(0);
                }}
                startIcon={<ReplayIcon sx={{ color: "#000000ff" }} />}
              >
                Tentar novamente
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    return null;
  };

  const errorNotification = (message) => {
    setError(true);

    setTimeout(() => {
      setError(false);
    }, 5000);

    notify("Error", message, true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "100vh", sm: "100vh" },
        backgroundColor: { xs: "#fff", sm: "#F1F5F9" },
      }}
    >
      <ReturnToLastPage />
      <Box
        sx={{
          minWidth: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          opacity: 0.8,
          flex: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            id="form"
            sx={{
              background: "#FFF",
              minHeight: {
                xs: "100vh", // Ocupa a altura inteira em telas pequenas
                sm: "100%", // Altura personalizada em telas maiores
                md: "583.2px",
                lg: "583.2px",
                xl: "583.2px",
              },
              minWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              maxWidth: {
                xs: "100%", // Ocupa a largura inteira em telas pequenas
                sm: "100%", // Largura personalizada em telas maiores
                md: "736.2px",
                lg: "736.2px",
                xl: "736.2px",
              },
              padding: "30px",
              borderRadius: {
                xs: "0", // Sem borda arredondada em telas pequenas
                sm: ".5rem", // Bordas arredondadas em telas médias ou maiores
              },
              boxShadow: {
                xs: "none", // Sem sombra em telas pequenas
                sm: "0 2px 4px -1px rgba(0, 0, 0, 0.1)", // Sombra em telas maiores
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: {
                xs: "flex-start",
                sm: "flex-start",
                md: "space-between",
                lg: "space-between",
              },
              height: "100%", // Faz com que o form ocupe toda a altura disponível
            }}
          >
            <FormContent
              currentStep={currentStep}
              steps={steps}
              instance={instance} // Pass instance as prop
              setInstance={setInstance} // Pass setInstance function as prop
              handleInstanceChange={(e) => setInstance(e.target.value)}
              openModal={() =>
                creatingCampaignWithHelpMode === true
                  ? setOpenModalCampaign(true)
                  : Navigate("/canais")
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "0px", sm: "10px" },
                marginTop: { xs: "30px", sm: "20px" },
                flexDirection: {
                  xs: "column", // Empilha os botões em telas menores
                  sm: "row", // Alinha os botões na horizontal em telas maiores
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  padding: "0 12px",
                  minWidth: {
                    xs: "100%",
                    sm: "0px",
                  },
                  height: "36px",
                  display:
                    currentStep === 1
                      ? "none"
                      : currentStep === 3 || currentStep === 4
                      ? "none"
                      : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginRight: "auto",
                }}
                onClick={() => setCurrentStep((prevStep) => prevStep - 1)}
              >
                Voltar
              </Button>
              <Tooltip
                title={
                  currentStep === 2 ? (
                    <Typography variant="body2">
                      Não avance antes de escanear o QR Code em seu WhatsApp
                    </Typography>
                  ) : null
                }
                arrow
                placement="top"
              >
                <Button
                  variant="contained"
                  sx={{
                    padding: "0 12px",
                    minWidth: {
                      xs: "100%",
                      sm: "0px", // Largura mínima em telas maiores
                    },
                    height: "36px",
                    display:
                      currentStep === steps.length
                        ? "none"
                        : (currentStep === 2 && qrcode === QRCODE2) ||
                          (currentStep === 2 && firstTime > 0)
                        ? "none"
                        : currentStep === 3 && poolingTimerExpired > 0
                        ? "none"
                        : "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "9999px",
                    fontSize: "12px",
                    fontWeight: 500,
                    marginLeft: "auto",
                    marginTop: { xs: "20px", sm: "0" },
                  }}
                  onClick={() => {
                    if (canMoveForward()) {
                      if (currentStep === 1) {
                        handleAdicionar();
                      }
                      setCurrentStep((prevStep) => prevStep + 1);
                    } else {
                      errorNotification("Preencha o nome do WhatsApp");
                    }
                  }}
                >
                  Próximo
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <CampaignModal
        cancelText="Adicionar mais um canal"
        confirmText="Retornar ao formulário"
        subTitle={
          "Percebemos que você está configurando uma campanha. Gostaria de retornar ao formulário?"
        }
        isOpen={openModalCampaign}
        onClose={() => setOpenModalCampaign(false)}
        onCancel={() => Navigate("/canais/adicionar")}
        onConfirm={() => (
          HandleChannels(), sessionStorage.setItem("ReturToCampaign", true)
        )}
        title={"Canal adicionado com sucesso"}
        iconColor={themeColors.purple400}
      />
    </Box>
  );
}

export default ChannelPageAddWhatsApp;
